import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { fetchUserPreferences, saveUserPreference } from '../lib/userPreferences';
import { accountGeneration, accountStorage, cachedUserIdOwner, scopeToAccount } from '../lib/accountStorage';
import { categoryForType, type NotificationCategory } from '../utils/notificationCategories';

// Notification preferences — a slice of the shared user_preferences row
// (notifications jsonb, mig 0235). Everything defaults ON; an absent key is ON,
// so existing users are unaffected until they opt out.

export interface InAppPrefs {
  social: boolean;
  messages: boolean;
  connections: boolean;
  invitations: boolean;
  /** What's new (Phase 2). ⚠️ An older app saves `inApp` without it — absent is ON. */
  updates: boolean;
}

const DEFAULT_IN_APP: InAppPrefs = { social: true, messages: true, connections: true, invitations: true, updates: true };

interface NotificationPrefsState {
  inApp: InAppPrefs;
  email: boolean;
  initializedUserId: string | null;
  cachedUserId: string | null;
  initialize: () => Promise<void>;
  setInApp: (cat: NotificationCategory, on: boolean) => void;
  setEmail: (on: boolean) => void;
  /** Should a notification of this raw type be surfaced in the bell? */
  isTypeEnabled: (type: string) => boolean;
}

function mergeInApp(raw: unknown): InAppPrefs {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Partial<InAppPrefs>;
  return {
    social: r.social !== false,
    messages: r.messages !== false,
    connections: r.connections !== false,
    invitations: r.invitations !== false,
    updates: r.updates !== false,
  };
}

export const useNotificationPrefsStore = create<NotificationPrefsState>()(
  persist<NotificationPrefsState, [], [], Pick<NotificationPrefsState, 'inApp' | 'email' | 'cachedUserId'>>(
    (set, get) => ({
      inApp: DEFAULT_IN_APP,
      email: true,
      initializedUserId: null,
      cachedUserId: null,

      initialize: async () => {
        const at = accountGeneration();
        const res = await fetchUserPreferences();
        // An answer for an account that has since signed out is not this copy's.
        if (at !== accountGeneration()) return;
        if (!res) return; // logged out
        if (get().initializedUserId === res.userId) return;
        set((state) => {
          const cacheIsOurs = state.cachedUserId === null || state.cachedUserId === res.userId;
          const blob = res.row?.notifications as { inApp?: unknown; email?: boolean } | null | undefined;
          if (blob && typeof blob === 'object') {
            return {
              inApp: mergeInApp(blob.inApp),
              email: blob.email !== false,
              initializedUserId: res.userId,
              cachedUserId: res.userId,
            };
          }
          // No cloud value yet: keep our cache if it's this user's, else defaults.
          return {
            inApp: cacheIsOurs ? state.inApp : DEFAULT_IN_APP,
            email: cacheIsOurs ? state.email : true,
            initializedUserId: res.userId,
            cachedUserId: res.userId,
          };
        });
      },

      setInApp: (cat, on) => {
        const inApp = { ...get().inApp, [cat]: on };
        set({ inApp });
        void saveUserPreference({ notifications: { inApp, email: get().email } });
      },

      setEmail: (on) => {
        set({ email: on });
        void saveUserPreference({ notifications: { inApp: get().inApp, email: on } });
      },

      isTypeEnabled: (type) => {
        const cat = categoryForType(type);
        return cat ? get().inApp[cat] : true; // unknown types always show
      },
    }),
    {
      name: 'notification-prefs-storage',
      // ⭐ One saved copy per account — see `scopeToAccount` below.
      storage: accountStorage(cachedUserIdOwner),
      version: 1,
      partialize: (s) => ({ inApp: s.inApp, email: s.email, cachedUserId: s.cachedUserId }),
    },
  ),
);

/* ⚠️⚠️ ONE SAVED COPY PER ACCOUNT (lib/accountStorage). The device kept one copy, tagged with the
   account it was loaded for, and a sign-out does not reload the page: the next account's bell filtered
   by the last one's switches until its own load, and its first switch before that load saved them —
   the whole set is written — into its own row. */
scopeToAccount(useNotificationPrefsStore);
